/**
 * Hook to hydrate auth state on app startup.
 * Verifies stored tokens are still valid by calling the profile endpoint.
 */

import { useEffect, useRef } from 'react';
import { useAuthStore } from '../stores';
import { defaultApiConfig, buildUrl, buildHeaders } from '@callbridge/ui';
import { API_BASE } from '../lib/apiBase';
import type { User } from '@callbridge/ui';

export function useAuthInit() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const accessToken = useAuthStore((s) => s.accessToken);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const loginSuccess = useAuthStore((s) => s.loginSuccess);
  const logout = useAuthStore((s) => s.logout);
  const startLoading = useAuthStore((s) => s.startLoading);
  const tokenRefreshed = useAuthStore((s) => s.tokenRefreshed);

  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    if (!isAuthenticated || !accessToken) return;

    async function verifySession() {
      startLoading();

      try {
        // Try current token
        const res = await fetch(buildUrl(API_BASE, '/users/me'), {
          headers: buildHeaders({ ...defaultApiConfig, getAccessToken: () => accessToken }, {}),
        });

        if (res.ok) {
          const data = (await res.json()) as { data: { user: User } };
          loginSuccess(data.data.user, accessToken!, refreshToken!);
          return;
        }

        // Try refresh
        if (refreshToken) {
          const refreshRes = await fetch(buildUrl(API_BASE, '/auth/refresh'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken }),
          });

          if (refreshRes.ok) {
            const data = (await refreshRes.json()) as {
              data: { tokens: { accessToken: string; refreshToken: string } };
            };
            tokenRefreshed(data.data.tokens.accessToken, data.data.tokens.refreshToken);
            return;
          }
        }

        // Both failed — logout
        logout();
      } catch {
        // Network error — keep current state (offline support)
      }
    }

    void verifySession();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}
